import * as React from 'react';
import { useMergeRefs } from 'use-callback-ref';
import { WithCommonProps } from '@types';
import { PopoutPlacement } from '@hooks';
import { Button, ButtonProps } from '@components/button';
import { IconNames, IconSizes, Icon } from '@components/icon';
import { Tooltip, useHoverableTooltip } from '@components/tooltip';

export interface ButtonIconProps extends WithCommonProps<ButtonProps> {
  /** Name of the icon to be displayed */
  icon: IconNames;
  /** Size of the icon */
  iconSize?: IconSizes;
  /** Solid or outlined icon flag */
  iconSolid?: boolean;
  /** Content to be rendered inside the tooltip */
  children?: React.ReactNode;
  /** ariaDescribedby to reference the tooltip id by accessibility */
  ariaDescribedby: string;
  /** Indicates the position of the popout */
  placement?: PopoutPlacement;
}

export const ButtonIcon = React.forwardRef(
  (
    props: ButtonIconProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ): JSX.Element => {
    const { triggerProps, opened } = useHoverableTooltip();
    const {
      icon,
      children,
      iconSize = 'lg',
      iconSolid = false,
      ariaDescribedby,
      placement,
      ...rest
    } = props;
    const newRef = React.useRef<HTMLButtonElement>(null);
    const buttonRef = useMergeRefs<HTMLButtonElement>([newRef, ref]);

    return (
      <>
        <Button
          {...triggerProps}
          {...rest}
          ref={buttonRef}
          aria-describedby={ariaDescribedby}
        >
          <Icon name={icon} size={iconSize} solid={iconSolid} />
        </Button>

        {children && (
          <Tooltip
            placement={placement}
            reference={buttonRef.current}
            id={ariaDescribedby}
            isOpen={opened}
          >
            {children}
          </Tooltip>
        )}
      </>
    );
  }
);

ButtonIcon.displayName = 'ButtonIcon';
